import React, { useCallback, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import { observer, Observer } from "mobx-react";
import { AppBar, Button, Fab, makeStyles } from "@material-ui/core";
import { Add, Hotel, PlayCircleFilled, Refresh, Flight } from "@material-ui/icons";
import { MarkHighlighter, WithTooltip } from "@components";
import { store } from "@store";
import { useStore } from "@hooks";
import locale from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import { TICKET_SLIDER, TicketProfileLocation } from "@navigation";
import { CategoryBadge, TodoCategory } from "@graphql";
import { stringNormalize } from "@bonliva-crm/common";

import SearchPageLayout from "@search/components/SearchPageLayout";
import SearchResultLayout from "@search/components/SearchResultLayout";
import { SortOption, SortOptions } from "@search/SortOptions";

import { TicketsSearchStore } from "./TicketsSearchStore";
import TicketsSearchForm from "./TicketsSearchForm";
import TicketCard from "./components/TicketCard";
import TicketSearchCategoryItem from "./components/TicketSearchCategoryItem";
import { ticketSearchFormInitialData } from "./TicketSearchFormData";
import { InfiniteScroll } from "@search/InfiniteScroll";
import { AccommodationRequestCreateLocation } from "@bookings/BookingHospitality/locations/AccommodationRequestCreateLocation";
import { AccommodationRequest } from "@bookings/BookingHospitality/AccommodationRequest/AccommodationRequest";
import { sortOptionDataBlank } from "@tickets/utils";
import { TravelRequestLocation } from "@bookings/BookingHospitality/locations/TravelRequestLocation";

type Props = RouteComponentProps<any>;

export function TicketsSearch({ match }: Props): React.ReactElement<Props> {
  const [selectedSortOption, setSelectedSortOption] = useState<SortOption>(
    sortOptionDataBlank[0]
  );
  const styles = useStyles({});
  const { navigation, todoStore } = store();
  const ticketSearchStore: TicketsSearchStore = useStore(
    () => new TicketsSearchStore({ initialFilter: ticketSearchFormInitialData })
  );

  const sortOptions = useMemo(
    () =>
      sortOptionDataBlank.map((item) => {
        return {
          ...item,
          active: selectedSortOption.value === item.value
        };
      }),
    [selectedSortOption]
  );

  const handleSortOptionClick = useCallback((option: SortOption) => {
    ticketSearchStore.sortTickets(option);
    setSelectedSortOption(option);
  }, []);

  const handleCategoryClick = useCallback(
    (badge: CategoryBadge) => {
      let categoryFilter: TodoCategory[];
      if (ticketSearchStore.filterManager.filter.category.includes(badge.Category)) {
        categoryFilter = [];
      } else {
        categoryFilter = [badge.Category];
      }

      ticketSearchStore.filterManager.updateFilter({
        category: categoryFilter
      });
    },
    [ticketSearchStore.filterManager.filter.category]
  );

  const renderRow = (item, index) => {
    const row = ticketSearchStore.searchManager.items[index];
    const { filter } = ticketSearchStore.filterManager;

    return (
      <div
        key={row._id}
        style={{
          padding: 8
        }}
      >
        <TicketCard
          item={row}
          onOpen={() =>
            navigation.open(TICKET_SLIDER, new TicketProfileLocation(row._id))
          }
          navigateTo={navigation.open}
          withAssignedBy={!filter.category.includes(TodoCategory.DelegatedByMe)}
          withAssignedTo={filter.category.includes(TodoCategory.DelegatedByMe)}
        />
      </div>
    );
  };

  const renderContent = (scrollContainer) => {
    return (
      <div>
        <Observer>
          {() => (
            <InfiniteScroll
              scrollContainer={scrollContainer}
              search={ticketSearchStore.filterManager?.filter.text}
              items={ticketSearchStore.searchManager.items}
              hasMore={ticketSearchStore.hasMore}
              loadMore={ticketSearchStore.searchManager.loadMoreRows}
              dataLength={ticketSearchStore.searchManager.rowCount}
              loading={ticketSearchStore.searchManager.isLoading}
              renderRow={renderRow}
            />
          )}
        </Observer>
      </div>
    );
  };

  const renderFilter = <TicketsSearchForm manager={ticketSearchStore.filterManager} />;

  const handleCreateDialogToggle = useCallback(() => {
    //TODO remove after migration to freshdesk
    window.open(`https://bonliva.freshdesk.com/a/tickets/new`, "_blank");
  }, []);

  const handleCreateAccommodationRequest = useCallback(() => {
    const request = AccommodationRequest.CreateNew("", "");

    navigation.open(
      "accommodation_request",
      new AccommodationRequestCreateLocation({
        bookingId: null,
        formProps: {
          bookingGroupId: null,
          isDisabled: false,
          initialValues: request,
          isNew: true
        }
      })
    );
  }, []);

  const handleCreateTravelRequest = useCallback(() => {
    navigation.open("travel_request", new TravelRequestLocation({}));
  }, []);

  return (
    <>
      <AppBar position={"sticky"} className={styles.appBar}>
        <Observer>
          {() => (
            <div className={styles.appBarContent}>
              {todoStore.badges &&
                todoStore.badges.Badges?.map((badge) => (
                  <TicketSearchCategoryItem
                    key={badge.Category}
                    badge={badge}
                    selectedTeam={todoStore.selectedTeam?.Name}
                    onClick={handleCategoryClick}
                    active={ticketSearchStore.filterManager.filter.category.includes(
                      badge.Category
                    )}
                  />
                ))}
            </div>
          )}
        </Observer>
      </AppBar>

      <SearchPageLayout
        delayed
        stickyContainerProps={{
          offsetTop: 50
        }}
        actionBar={
          <div className={styles.actionBarWrapper}>
            <div className={styles.actionBarBtn}>
              <WithTooltip title={locale["Add new ticket"]}>
                <Fab color="default" onClick={handleCreateDialogToggle} size="small">
                  <Add />
                </Fab>
              </WithTooltip>

              <WithTooltip title={locale.newAccommodationRequest}>
                <Fab
                  color="primary"
                  onClick={handleCreateAccommodationRequest}
                  size="small"
                >
                  <Hotel />
                </Fab>
              </WithTooltip>
              <WithTooltip title={locale.newTravelRequest}>
                <Fab color="primary" onClick={handleCreateTravelRequest} size="small">
                  <Flight />
                </Fab>
              </WithTooltip>
            </div>

            <Observer>
              {() => (
                <div className={styles.totalBtnWrap}>
                  <WithTooltip title={locale.RefreshListing}>
                    <Button onClick={() => ticketSearchStore.fetch()}>
                      <Refresh />
                    </Button>
                  </WithTooltip>

                  {ticketSearchStore.isShowTodoTotal ? (
                    <Button>
                      {locale.Total}: {ticketSearchStore.todoTotal}
                    </Button>
                  ) : (
                    <Button
                      onClick={ticketSearchStore.showTodoTotal}
                      startIcon={<PlayCircleFilled className={styles.totalIcon} />}
                    >
                      {locale.seeQuantity}
                    </Button>
                  )}
                </div>
              )}
            </Observer>
            <SortOptions onSortOption={handleSortOptionClick} sortOptions={sortOptions} />
          </div>
        }
        content={(scrollContainer) => (
          <Observer>
            {() => (
              <SearchResultLayout fittedContent>
                {renderContent(scrollContainer)}
              </SearchResultLayout>
            )}
          </Observer>
        )}
        filter={renderFilter}
      />

      <MarkHighlighter
        highlightSearch={[
          ticketSearchStore.filterManager.filter.text,
          stringNormalize(ticketSearchStore.filterManager.filter.text)
        ]}
        getMarkjsContext={() => document.querySelectorAll(".hl")}
      />
    </>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  actionBarWrapper: {
    ...theme.fixedGrid,
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),

    alignItems: "center",
    display: "flex",
    justifyContent: "space-between"
  },
  actionBarBtn: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1)
  },
  appBar: {
    flex: "0 0",
    backgroundColor: theme.palette.background.paper,
    padding: "10px 20px",
    overflow: "unset !important",
    "z-index": "4"
  },
  appBarContent: {
    display: "flex",
    justifyContent: "flex-start",
    ...theme.fixedGrid,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: "100%"
  },
  actionsWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: theme.spacing(2)
  },
  totalBtnWrap: {
    display: "flex",
    gap: 15,
    marginLeft: "auto",
    marginRight: 30
  },
  totalIcon: {
    color: theme.palette.success.main
  }
}));

export default observer(TicketsSearch);
